import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../AuthProvider";

const fmtInt = (n) => Number(n || 0).toLocaleString("id-ID");
const fmtMoney = (n) => Math.round(Number(n || 0)).toLocaleString("id-ID");
const fmtPct = (n) => {
  const x = Number(n || 0);
  return `${x.toFixed(2)}%`;
};

const SIGNAL_LABEL = {
  HOLD: "Hold",
  ADD: "Add Lot",
  TAKE_PROFIT: "Take Profit",
  CUT_LOSS: "Sell (CL)",
  REVIEW: "Review",
};

function getBrokerAllAction(flags) {
  if (!Array.isArray(flags)) return null;
  if (flags.includes("NET_ALL_BIG_ACC_PLUS_3")) return "BIG_ACC";
  if (flags.includes("NET_ALL_BIG_DIST_MINUS_3")) return "BIG_DIST";
  return null;
}

function computePortfolioAction({ v1Signal, suggestPct, brokerAllAction, pnlPct }) {
  const sig = String(v1Signal || "").toUpperCase();
  const s = Number(suggestPct ?? 0);
  const p = Number(pnlPct ?? 0);

  // tweak cepat
  const CUTLOSS_PCT = -7;
  const TAKEPROFIT_PCT = 8;
  const ADD_MIN_SUGGEST = 12;
  const REVIEW_BROKER_DIST_PCT = 3;

  if (p <= CUTLOSS_PCT) {
    return { action: "CUT_LOSS", reason: `P/L ${p.toFixed(2)}% <= ${CUTLOSS_PCT}%` };
  }

  if (p >= TAKEPROFIT_PCT) {
    return { action: "TAKE_PROFIT", reason: `P/L ${p.toFixed(2)}% >= ${TAKEPROFIT_PCT}%` };
  }

  if (brokerAllAction === "BIG_DIST") {
    if (p >= REVIEW_BROKER_DIST_PCT) {
      return { action: "TAKE_PROFIT", reason: `Broker ALL BIG_DIST saat profit ${p.toFixed(2)}%` };
    }
    return { action: "REVIEW", reason: "Broker ALL BIG_DIST (distribusi)" };
  }

  if (brokerAllAction === "BIG_ACC" && sig === "BUY" && s >= ADD_MIN_SUGGEST) {
    return { action: "ADD", reason: `BUY + suggest ${s}% + Broker ALL BIG_ACC` };
  }

  if (sig === "AVOID") return { action: "REVIEW", reason: "V1 = AVOID" };
  if (sig === "WATCH") return { action: "HOLD", reason: "V1 = WATCH" };

  return { action: "HOLD", reason: "Normal" };
}

function ActionBadge({ action }) {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border";
  const cls =
    action === "ADD" ? "border-emerald-700/60 bg-emerald-900/20 text-emerald-200" :
    action === "TAKE_PROFIT" ? "border-amber-700/60 bg-amber-900/20 text-amber-200" :
    action === "CUT_LOSS" ? "border-red-700/60 bg-red-900/20 text-red-200" :
    action === "REVIEW" ? "border-sky-700/60 bg-sky-900/20 text-sky-200" :
    "border-gray-700 bg-gray-900/20 text-gray-200";

  return <span className={`${base} ${cls}`}>{SIGNAL_LABEL[action] || action}</span>;
}


export default function PortfolioPage() {
  const { token } = useAuth() || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const nav = useNavigate();
  const goDetail = (code) => {
    const sym = (code || "").toUpperCase().trim();
    if (!sym) return;
    nav(`/stocks/${encodeURIComponent(sym)}`);
  };
  
  const [openSym, setOpenSym] = useState(null); // symbol yang sedang expand (mobile)

  // modal state sederhana (MVP)
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null); // row or null
  const [form, setForm] = useState({ kode_saham: "", balance_lot: 0, available_lot: "", avg_price: 0 });

  const fetchData = async () => {
    setLoading(true); setErr(null);
    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch("/api/portfolio", { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Failed to load portfolio");
      setRows(json.rows || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const DetailRow = ({ label, value, valueClass = "" }) => (
    <div className="flex items-center justify-between py-1 text-xs">
      <div className="text-gray-400">{label}</div>
      <div className={`text-gray-200 font-medium ${valueClass}`}>{value}</div>
    </div>
  );

  useEffect(() => { fetchData(); }, [token]);

  const computed = useMemo(() => {
    return (rows || []).map((r) => {
      const lot = Number(r.balance_lot || 0);
      const avg = Number(r.avg_price || 0);
      const cur = Number(r.current_price || 0);

      const invested = lot * 100 * avg;
      const marketValue = lot * 100 * cur;
      const pnl = marketValue - invested;
      const pct = invested > 0 ? (pnl / invested) * 100 : 0;

      return { ...r, invested, marketValue, pnl, pct };
    });
  }, [rows]);
  
  const summary = useMemo(() => {
    const netPL = (computed || []).reduce((sum, r) => sum + Number(r.pnl || 0), 0);
    const totalEquity = (computed || []).reduce((sum, r) => sum + Number(r.marketValue || 0), 0);
    return { netPL, totalEquity };
  }, [computed]);
  
  const openAdd = () => {
    setEdit(null);
    setForm({ kode_saham: "", balance_lot: 0, available_lot: "", avg_price: 0 });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEdit(r);
    setForm({
      kode_saham: r.kode_saham,
      balance_lot: r.balance_lot,
      available_lot: r.available_lot,
      avg_price: r.avg_price,
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      kode_saham: String(form.kode_saham || "").trim().toUpperCase(),
      balance_lot: Number(form.balance_lot || 0),
      avg_price: Number(form.avg_price || 0),
      // available_lot optional; kalau kosong biar backend auto=balance
      ...(String(form.available_lot).trim() !== "" ? { available_lot: Number(form.available_lot || 0) } : {}),
    };

    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const url = edit ? `/api/portfolio/${edit.id}` : `/api/portfolio`;
    const method = edit ? "PUT" : "POST";

    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    const json = await res.json();
    if (!res.ok) {
      alert(json.detail || "Failed");
      return;
    }
    setOpen(false);
    await fetchData();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this position?")) return;

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`/api/portfolio/${id}`, {
      method: "DELETE",
      headers,
    });

    const json = await res.json();
    if (!res.ok) {
      window.alert(json.detail || "Failed");
      return;
    }

    await fetchData();
  };


  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-lg font-semibold text-white">Portfolio</div>
          <div className="text-xs text-gray-400">P/L dihitung otomatis dari harga terbaru daily_stocks</div>
        </div>
        <button
          onClick={openAdd}
          className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm text-white"
        >
          + Add
        </button>
      </div>

      {loading && <div className="text-xs text-gray-400">Loading…</div>}
      {err && <div className="text-xs text-red-300">Error: {err}</div>}

      {/* ===== Summary ===== */}
      <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
          <div className="text-xs text-gray-400">Net Profit / Loss</div>
          <div className={`mt-1 text-xl font-semibold ${summary.netPL >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {summary.netPL >= 0 ? "+" : ""}{fmtMoney(summary.netPL)}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">Akumulasi P/L semua posisi</div>
        </div>
      
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
          <div className="text-xs text-gray-400">Total Equity</div>
          <div className="mt-1 text-xl font-semibold text-gray-100">
            {fmtMoney(summary.totalEquity)}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">Total market value portofolio</div>
        </div>
      </div>
      
      {/* ================= DESKTOP TABLE (md+) ================= */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-800 bg-gray-900">
        <table className="min-w-[1100px] w-full text-sm">
          <thead className="bg-gray-950/40 text-gray-300">
            <tr>
              <th className="text-left px-3 py-2">Symbol</th>
              <th className="text-right px-3 py-2">Lot</th>
              <th className="text-right px-3 py-2">Avg Price</th>
              <th className="text-right px-3 py-2">Price</th>
              <th className="text-right px-3 py-2">Invested</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">P/L</th>
              <th className="text-right px-3 py-2">Percentage</th>
              <th className="text-left px-3 py-2">Bot Signal</th>
              <th className="text-center px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {computed.map((r) => {
              const pnlColor =
                r.pnl > 0 ? "text-emerald-300" : r.pnl < 0 ? "text-red-300" : "text-gray-300";

              const brokerAllAction = getBrokerAllAction(r.flags);
              const bot = computePortfolioAction({
                v1Signal: r.signal_v1,
                suggestPct: r.suggest_pct,
                brokerAllAction,
                pnlPct: r.pct,
              });

              return (
                <tr key={r.id} className="border-t border-gray-800">
                  <td className="px-3 py-2 font-semibold">
                    <button
                      onClick={() => goDetail(r.kode_saham)}
                      className="text-indigo-300 hover:text-indigo-200 hover:underline"
                      title="Open stock detail"
                    >
                      {String(r.kode_saham || "").toUpperCase()}
                    </button>
                  </td>                  
                  <td className="px-3 py-2 text-right">{fmtInt(r.balance_lot)}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(r.avg_price)}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(r.current_price)}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(r.invested)}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(r.marketValue)}</td>
                  <td className={`px-3 py-2 text-right ${pnlColor}`}>{fmtMoney(r.pnl)}</td>
                  <td className={`px-3 py-2 text-right ${pnlColor}`}>{fmtPct(r.pct)}</td>

                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <ActionBadge action={bot.action} />
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openEdit(r)}
                        className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs"
                      >
                        ⋮
                      </button>
                      <button
                        onClick={() => del(r.id)}
                        className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-xs text-white"
                        title="Delete"
                      >
                        X
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {computed.length === 0 && !loading && (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center text-xs text-gray-400">
                  Belum ada posisi portfolio. Klik <span className="text-gray-200 font-semibold">+ Add</span>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE VIEW (<md): COMPACT LIST + EXPAND ================= */}
      <div className="md:hidden space-y-2">
        {computed.map((r) => {
          const pnlColor =
            r.pnl > 0 ? "text-emerald-300" : r.pnl < 0 ? "text-red-300" : "text-gray-300";

          const isOpen = openSym === r.kode_saham;

          return (
            <div key={r.id} className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
              {/* Header row (compact) */}
              <button
                onClick={() => setOpenSym(isOpen ? null : r.kode_saham)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
              >
                <div>
                  <div className="flex items-center gap-2">

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();     // ✅ NO toggle expand
                        goDetail(r.kode_saham);  // ✅ navigate ke detail saham
                      }}
                      className="text-sm font-semibold text-blue-300 hover:text-blue-200"
                      title="Buka detail saham"
                    >
                      {r.kode_saham}
                    </button>

                    {(() => {
                      const brokerAllAction = getBrokerAllAction(r.flags);
                      const bot = computePortfolioAction({
                        v1Signal: r.signal_v1,
                        suggestPct: r.suggest_pct,
                        brokerAllAction,
                        pnlPct: r.pct,
                      });
                      return <ActionBadge action={bot.action} />;
                    })()}
                  </div>

                  <div className="text-[11px] text-gray-400">
                    Invested: <span className="text-gray-200">{fmtMoney(r.invested)}</span>
                  </div>
                </div>

                <div className="text-right">
                  <div className={`text-sm font-semibold ${pnlColor}`}>
                    {fmtMoney(r.pnl)}
                  </div>
                  <div className={`text-[11px] ${pnlColor}`}>
                    {fmtPct(r.pct)}
                  </div>
                </div>
              </button>

              {/* Expanded detail */}
              {isOpen && (
                <div className="border-t border-gray-800 px-4 py-3">
                  {/* bot */}
                  {(() => {
                    const brokerAllAction = getBrokerAllAction(r.flags);
                    const bot = computePortfolioAction({
                      v1Signal: r.signal_v1,
                      suggestPct: r.suggest_pct,
                      brokerAllAction,
                      pnlPct: r.pct,
                    });
                    return (
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-xs text-gray-400">Bot Signal</div>
                        <ActionBadge action={bot.action} />
                      </div>
                    );
                  })()}

                  <DetailRow label="Balance Lot" value={fmtInt(r.balance_lot)} />
                  <DetailRow label="Average Price" value={fmtMoney(r.avg_price)} />
                  <DetailRow label="Current Price" value={fmtMoney(r.current_price)} />
                  <DetailRow label="Market Value" value={fmtMoney(r.marketValue)} />
                  <DetailRow label="P/L (Rp)" value={fmtMoney(r.pnl)} valueClass={pnlColor} />
                  <DetailRow label="Gain/Loss (%)" value={fmtPct(r.pct)} valueClass={pnlColor} />

                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEdit(r)}
                      className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-xs text-gray-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => del(r.id)}
                      className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-xs text-white"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {computed.length === 0 && !loading && (
          <div className="rounded-xl border border-gray-800 bg-gray-900 px-4 py-8 text-center text-xs text-gray-400">
            Belum ada posisi portfolio. Klik <span className="text-gray-200 font-semibold">+ Add</span>.
          </div>
        )}
      </div>


      {/* Modal sederhana */}
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-800 bg-gray-900 p-4">
            <div className="text-sm font-semibold text-white mb-3">
              {edit ? "Edit Position" : "Add Position"}
            </div>

            <div className="space-y-2 text-sm">
              <div>
                <div className="text-xs text-gray-400 mb-1">Symbol</div>
                <input
                  value={form.kode_saham}
                  onChange={(e) => setForm((p) => ({ ...p, kode_saham: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100"
                  placeholder="BBCA"
                />
              </div>

							<div>
								<div className="text-xs text-gray-400 mb-1">Balance Lot</div>
								<input
									type="number"
									value={form.balance_lot}
									onChange={(e) => setForm((p) => ({ ...p, balance_lot: e.target.value }))}
									className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100"
								/>
							</div>

              <div>
                <div className="text-xs text-gray-400 mb-1">Average Price</div>
                <input
                  type="number"
                  value={form.avg_price}
                  onChange={(e) => setForm((p) => ({ ...p, avg_price: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={save}
                className="px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-sm text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
